import { ApplicationStatus, ApplicationWithDetail, UpdateApplicationStatus } from "@humanex/interfaces";
import { Avatar } from "@humanex/ui/components";
import { useI18n } from "@humanex/ui/libs";
import { createFileRoute, useParams, useRouteContext } from "@tanstack/react-router";

import {
  useApplicationsByProgramQuery,
  useUpdateApplicationStatus
} from "@/hooks/queries/use-application-query";

export const Route = createFileRoute("/_authenticated/org/$orgId/program/$programId/applicants/")({
  component: RouteComponent
});

const STATUS_LABELS: Record<string, string> = {
  DEPOSIT_PENDING: "Chờ thanh toán",
  SUBMITTED: "Đã nộp hồ sơ",
  UNDER_REVIEW: "Đang xét duyệt",
  NEEDS_SUPPLEMENT: "Cần bổ sung",
  ACCEPTED: "Trúng tuyển",
  REJECTED: "Không đạt",
  CANCELLED: "Đã hủy"
};

const ALL_STATUSES = Object.values(ApplicationStatus);

function RouteComponent() {
  const { programId } = useParams({
    from: "/_authenticated/org/$orgId/program/$programId/applicants/"
  });
  const { program } = useRouteContext({
    from: "/_authenticated/org/$orgId/program/$programId/applicants/"
  });
  const { formatDate } = useI18n();

  const { data, isLoading } = useApplicationsByProgramQuery(programId);
  const { mutate: updateStatus } = useUpdateApplicationStatus();

  const applications = data?.data ?? [];

  const handleStatusChange = (application: ApplicationWithDetail, status: string) => {
    const data: UpdateApplicationStatus = {
      status: status as ApplicationStatus
    };
    updateStatus({ id: application.id, data });
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-responsive-xl font-normal text-text-primary">Ứng viên</p>
        <p className="text-responsive-base text-text-secondary mt-1">{program.title}</p>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-text-secondary">Đang tải...</div>
      ) : applications.length === 0 ? (
        <div className="py-12 text-center border border-border-light rounded-lg bg-background-secondary/30">
          <p className="text-text-secondary">Chưa có ứng viên nào nộp hồ sơ.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-border-light rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-light bg-background-secondary">
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Ứng viên</th>
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Ngày nộp</th>
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Trạng thái</th>
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Ghi chú</th>
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((application) => {
                const candidateName = application.candidate.user?.name ?? "—";
                const candidateAvatar = application.candidate.user?.avatar ?? undefined;
                return (
                  <tr
                    key={application.id}
                    className="border-b border-border-light hover:bg-background-secondary/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar src={candidateAvatar} name={candidateName} size="sm" />
                        <div>
                          <p className="text-text-primary font-medium">{candidateName}</p>
                          <p className="text-text-secondary text-xs">
                            {application.candidate.user?.email ?? "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-text-secondary">
                      {application.submittedAt
                        ? formatDate(application.submittedAt, {
                            month: "short",
                            day: "numeric",
                            year: "numeric"
                          })
                        : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          application.status === ApplicationStatus.ACCEPTED
                            ? "bg-success-100 text-success-700"
                            : application.status === ApplicationStatus.REJECTED
                              ? "bg-error-100 text-error-700"
                              : application.status === ApplicationStatus.CANCELLED
                                ? "bg-gray-100 text-gray-600"
                                : "bg-secondary-100 text-secondary-700"
                        }`}
                      >
                        {STATUS_LABELS[application.status] ?? application.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-text-secondary text-xs max-w-[200px] truncate">
                      {application.reviewNotes ?? application.supplementReq ?? "—"}
                    </td>
                    <td className="py-3 px-4">
                      <select
                        className="text-sm border border-border-light rounded px-2 py-1 bg-background text-text-primary focus:outline-none"
                        value={application.status}
                        onChange={(e) => handleStatusChange(application, e.target.value)}
                      >
                        {ALL_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABELS[s] ?? s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
